import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../../i18n/LanguageContext';
import { MAP_LINK, PHONE_TEL, WHATSAPP } from '../../data/contact';
import { short, sipValue } from '../../utils/money';
import Reveal from '../layout/Reveal';
import Faq from '../layout/Faq';
import { IMG } from '../../data/media';

const CMP = {
  en: [['A person you can meet in Haldia', '✓', '✗'], ['Help with claim paperwork', '✓', 'You do it yourself'], ['Explained in Bengali', '✓', 'Mostly English'], ['Renewal reminders on WhatsApp', '✓', 'App or email'], ['All your policies reviewed together', '✓', 'One at a time']],
  bn: [['হলদিয়ায় সামনাসামনি দেখা করা যায়', '✓', '✗'], ['ক্লেমের কাগজপত্রে সাহায্য', '✓', 'নিজেকেই করতে হয়'], ['বাংলায় বুঝিয়ে বলা', '✓', 'বেশিরভাগ ইংরেজিতে'], ['হোয়াটসঅ্যাপে রিনিউয়ালের রিমাইন্ডার', '✓', 'অ্যাপ বা ইমেল'], ['সব পলিসি একসঙ্গে রিভিউ', '✓', 'একটা একটা করে']],
};

export function CompareTable() {
  const { lang, t } = useLang();
  return (
    <section className="py-20 sm:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Reveal className="text-center mb-10"><p className="kicker text-brand">{t('cKick')}</p><h2 className="h2">{t('cTitle')}</h2></Reveal>
        <Reveal className="rounded-3xl border border-slate-200 overflow-hidden shadow-sm text-sm sm:text-base">
          <div className="grid grid-cols-[1.4fr_1fr_1fr]">
            <div className="p-3 sm:p-4 bg-slate-50" />
            <div className="p-3 sm:p-4 bg-brand text-white font-bold text-center">Kalyani</div>
            <div className="p-3 sm:p-4 bg-slate-50 font-bold text-center text-slate-500">{t('cOnline')}</div>
          </div>
          {CMP[lang].map(([a, b, c]) => (
            <div key={a} className="grid grid-cols-[1.4fr_1fr_1fr] border-t border-slate-200">
              <div className="p-3 sm:p-4 font-medium">{a}</div>
              <div className="p-3 sm:p-4 text-center font-bold text-emerald-600 bg-brand/[0.04]">{b}</div>
              <div className="p-3 sm:p-4 text-center text-slate-400">{c}</div>
            </div>
          ))}
        </Reveal>
        <p className="mt-3 text-center text-xs text-slate-400">{t('cNote')}</p>
      </div>
    </section>
  );
}

export function SipTeaser() {
  const { lang, t } = useLang();
  const [amt, setAmt] = useState(5000);
  const [yrs, setYrs] = useState(15);
  const fv = sipValue(amt, 12, yrs);
  const inv = amt * 12 * yrs;
  const C = 2 * Math.PI * 48;
  return (
    <section className="py-20 sm:py-24 bg-mint">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
        <Reveal>
          <p className="kicker text-emerald-700">{t('sipKick')}</p>
          <h2 className="h2">{t('sipTitle')}</h2>
          <label className="block mt-8"><div className="flex justify-between text-sm font-semibold mb-2"><span>{t('sipAmt')}</span><span>₹{amt.toLocaleString('en-IN')}</span></div>
            <input type="range" min="500" max="50000" step="500" value={amt} onChange={(e) => setAmt(+e.target.value)} className="w-full accent-emerald-600" /></label>
          <label className="block mt-5"><div className="flex justify-between text-sm font-semibold mb-2"><span>{t('sipYrs')}</span><span>{yrs} {t('yrs')}</span></div>
            <input type="range" min="1" max="30" value={yrs} onChange={(e) => setYrs(+e.target.value)} className="w-full accent-emerald-600" /></label>
          <Link to="/calculators" className="inline-flex mt-8 bg-ink text-white font-bold px-5 py-3 rounded-full">{t('sipAll')}</Link>
        </Reveal>
        <Reveal className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-8">
          <svg viewBox="0 0 120 120" className="w-40 h-40 sm:w-44 sm:h-44 -rotate-90 shrink-0" aria-hidden="true">
            <circle cx="60" cy="60" r="48" fill="none" stroke="#DCEFE5" strokeWidth="16" />
            <circle cx="60" cy="60" r="48" fill="none" stroke="#1D5FBF" strokeWidth="16" strokeDasharray={`${(C * inv) / fv} ${C}`} style={{ transition: 'stroke-dasharray .6s ease' }} />
          </svg>
          <div className="space-y-3 w-full">
            <div><div className="text-xs text-slate-500 flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-brand" />{t('sipInv')}</div><div className="text-xl font-extrabold">{short(inv, lang)}</div></div>
            <div><div className="text-xs text-slate-500 flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#DCEFE5] border border-emerald-300" />{t('sipGain')}</div><div className="text-xl font-extrabold text-emerald-600">{short(fv - inv, lang)}</div></div>
            <div className="pt-3 border-t border-slate-100"><div className="text-xs text-slate-500">{t('sipVal')}</div><div className="text-3xl font-extrabold">{short(fv, lang)}</div></div>
            <p className="text-[11px] text-slate-400">{t('mfRiskShort')}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function ReviewsAndPartners() {
  const { t } = useLang();
  const logos = Array.from({ length: 8 }, (_, i) => i + 1);
  return (
    <>
      <section className="py-20 sm:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div><p className="kicker text-saffron">{t('rKick')}</p><h2 className="h2">{t('rTitle')}</h2></div>
            <div className="bg-white rounded-2xl shadow-sm px-4 py-3 text-sm self-start sm:self-auto"><span className="font-extrabold text-lg">G ★ —</span> <span className="text-slate-500">{t('rRating')}</span></div>
          </Reveal>
          <Reveal className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-3xl p-6 shadow-sm">
                <div className="text-amber-400" aria-hidden="true">★★★★★</div>
                <div className="mt-3 space-y-2"><div className="h-3 rounded bg-slate-100" /><div className="h-3 rounded bg-slate-100 w-11/12" /><div className="h-3 rounded bg-slate-100 w-4/5" /></div>
                <div className="mt-5 flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-slate-100" /><div className="text-sm text-slate-400">{t('rPh')}</div></div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
      <section className="py-14 bg-slate-100 overflow-hidden">
        <p className="text-center text-sm font-semibold text-slate-500 mb-6 px-4">{t('partners')}</p>
        <div className="flex w-max marquee gap-4" aria-hidden="true">
          {[...logos, ...logos].map((n, i) => (
            <div key={i} className="shrink-0 w-36 sm:w-40 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-400">{t('logoPh')} {n}</div>
          ))}
        </div>
      </section>
    </>
  );
}

const FAQ = {
  en: [['Is the first consultation really free?', 'Yes. We look at your needs and your existing policies at no charge, with no obligation to buy.'], ['Do you help when I need to make a claim?', 'Yes. We help you with the forms, documents and follow-up with the insurer.'], ['Can I pay my premium online?', 'Yes, on the Pay Premium page. We never ask for your OTP or PIN.'], ['Which insurers and fund houses do you work with?', 'We will share the current list on a call. (List to be confirmed by Kalyani Consultancy.)']],
  bn: [['প্রথম পরামর্শ কি সত্যিই বিনামূল্যে?', 'হ্যাঁ। কোনো খরচ বা কেনার বাধ্যবাধকতা ছাড়াই আমরা আপনার প্রয়োজন ও পুরনো পলিসি দেখে দিই।'], ['ক্লেমের সময় কি আপনারা সাহায্য করেন?', 'হ্যাঁ। ফর্ম, কাগজপত্র ও বিমা সংস্থার সঙ্গে যোগাযোগে আমরা সাহায্য করি।'], ['প্রিমিয়াম কি অনলাইনে দেওয়া যায়?', 'হ্যাঁ, "প্রিমিয়াম জমা দিন" পাতা থেকে। আমরা কখনো OTP বা PIN চাই না।'], ['কোন কোন বিমা সংস্থা ও ফান্ড হাউসের সঙ্গে কাজ করেন?', 'ফোনে বর্তমান তালিকা জানিয়ে দেব। (তালিকা কল্যাণী কনসালটেন্সি নিশ্চিত করবে।)']],
};

export function HomeFaqAndVisit() {
  const { lang, t } = useLang();
  return (
    <>
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10"><h2 className="h2">{t('faqTitle')}</h2></Reveal>
          <Reveal><Faq items={FAQ[lang]} /></Reveal>
        </div>
      </section>
      <section id="visit" className="relative min-h-[520px] flex items-center">
        <img src={IMG.office} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 py-16">
          <Reveal className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 max-w-md">
            <h2 className="text-2xl sm:text-3xl font-extrabold">{t('vTitle')}</h2>
            <p className="mt-3 text-slate-600">{t('addr')}</p>
            <p className="mt-1 text-sm text-slate-500">{t('hours')}</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <a href={PHONE_TEL} className="text-center bg-ink text-white font-bold py-3 rounded-xl">📞 {t('call')}</a>
              <a href={WHATSAPP} className="text-center bg-emerald-500 text-white font-bold py-3 rounded-xl">💬 WhatsApp</a>
            </div>
            <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" className="mt-3 block text-center text-sm font-semibold text-brand">{t('map')}</a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
